import {
  faCheck,
  faCircleCheck,
  faCircleXmark,
  faEllipsisV,
  faPen,
  faSync,
  faTrashAlt,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useTheme
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useMemo, useState } from 'react';

import DeviceStatusChip from '@/components/DeviceStatusChip';
import CellRendererErrorBoundary from '@/features/DevicesTable/components/CellRendererErrorBoundary';
import StatusIconWithTooltip from '@/features/DevicesTable/components/StatusIconWithTooltip';
import SOURCE_TYPE, {
  isReadOnlyManagedSourceType
} from '@/features/SourceCreate/SourceCreate.constants';

import useCloudSourcesTableCellRenderersStyles from './CloudSourcesTable.cellRenderers.styles';
import type {
  ActionMenuItem,
  ActionMenuRendererProps,
  ActionMenuRowData
} from './CloudSourcesTable.cellRenderers.types';
import useCloudSourcesTableStyles from './CloudSourcesTable.styles';
import type {
  ActionMenuType,
  CloudSourceCellRendererParams
} from './CloudSourcesTable.types';

/**
 * Cell renderer for the Name column with status icon prefix
 * This renderer displays a status icon with tooltip before the source name
 */
export const NameCellRenderer = (params: CloudSourceCellRendererParams) => {
  const { data } = params;
  const { classes } = useCloudSourcesTableCellRenderersStyles();

  if (!data) {
    return null;
  }

  const tooltipClasses = {
    arrow: classes.routeButtonTooltipArrow,
    tooltip: classes.routeButtonTooltip
  };

  return (
    <Stack
      direction="row"
      sx={{ width: '100%', overflow: 'hidden', alignItems: 'center' }}
    >
      <StatusIconWithTooltip
        status={data.status!}
        statusLabel={data.statusLabel}
        deviceType="Cloud Collector"
        tooltipClasses={tooltipClasses}
        iconClassName={classes.deviceStatusIconStyle}
      />
      <Tooltip title={data?.name}>
        <Box component="div" className={classes.icoLabelStyle}>
          {data.name}
        </Box>
      </Tooltip>
    </Stack>
  );
};

/**
 * Renders active/inactive state with icons
 */
export const ActiveStateCellRenderer = (
  params: CloudSourceCellRendererParams
) => {
  const { data } = params;
  const theme = useTheme();

  if (!data) {
    return null;
  }

  return (
    <Box>
      {data.active ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 6 }} />
          Active
        </Box>
      ) : (
        <Box
          sx={{
            color: theme.vars.palette.text.disabled,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <FontAwesomeIcon icon={faXmark} style={{ marginRight: 6 }} />
          Inactive
        </Box>
      )}
    </Box>
  );
};

/**
 * Renders cloud source status with optional tooltip
 */
export const StatusCellRenderer = (params: CloudSourceCellRendererParams) => {
  const { data } = params;
  const { classes } = useCloudSourcesTableStyles({ enableTimeFilter: false });

  if (!data) {
    return null;
  }

  return (
    <CellRendererErrorBoundary>
      <Box sx={{ whiteSpace: 'nowrap' }}>
        {!data.categoryFailedTooltip && (
          <DeviceStatusChip status={data.status ?? ''} />
        )}
        {data.categoryFailedTooltip && (
          <Tooltip
            arrow
            placement="right"
            title={data.categoryFailedTooltip}
            classes={{
              arrow: classes.routeButtonTooltipArrow,
              tooltip: classes.routeButtonTooltip
            }}
          >
            <DeviceStatusChip status={data.status ?? ''} />
          </Tooltip>
        )}
      </Box>
    </CellRendererErrorBoundary>
  );
};

/**
 * Action menu renderer with edit, activate/deactivate, delete, and resubmit options
 * Reads hasUpdatePermission, selectedOrgData, and onMenuClick from context or props
 */
export function ActionMenuRenderer<T extends ActionMenuRowData>(
  props: ActionMenuRendererProps<T>
) {
  // Read from context (preferred) or fallback to direct props for backward compatibility
  const hasUpdatePermission =
    props.context?.hasUpdatePermission ?? props.hasUpdatePermission ?? false;
  const selectedOrgData =
    props.context?.selectedOrgData ?? props.selectedOrgData;
  const onMenuClick = props.context?.onMenuClick ?? props.onMenuClick;

  const { data } = props;
  const { classes } = useCloudSourcesTableCellRenderersStyles();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const menuItems = useMemo(() => {
    if (!data || !hasUpdatePermission) {
      return [];
    }

    const concreteSource =
      data.type === 'provider'
        ? data.providerData
        : (data.sourceData ?? data.providerData);

    const rowActionModel = data.rowActionModel ?? {
      active: concreteSource?.active ?? data.active ?? false,
      resubmit: concreteSource?.resubmit ?? data.resubmit ?? false,
      statusCode:
        concreteSource?.source_statuses?.[0]?.status_code ??
        data.source_statuses?.[0]?.status_code ??
        0,
      sourceTypeId: concreteSource?.source_type_id ?? data.source_type_id,
      parentSourceUid:
        concreteSource?.parent_source_uid ?? data.parent_source_uid ?? null,
      allowSourceActivation:
        concreteSource?.allowSourceActivation ?? data.allowSourceActivation
    };

    const enableResubmit =
      !rowActionModel.resubmit &&
      rowActionModel.active &&
      rowActionModel.statusCode !== 1800 &&
      rowActionModel.statusCode !== 2001 &&
      rowActionModel.statusCode !== 3004;

    const isReadOnlyManaged = isReadOnlyManagedSourceType(
      rowActionModel.sourceTypeId
    );
    if (isReadOnlyManaged) {
      return [];
    }

    const items: ActionMenuItem[] = [];

    // Edit
    if (rowActionModel.parentSourceUid == null) {
      items.push({
        key: 'edit',
        label: 'Edit',
        icon: faPen
      });
    }

    // Activate / Deactivate
    items.push({
      key: rowActionModel.active ? 'deactivate' : 'activate',

      label: rowActionModel.active ? 'Deactivate' : 'Activate',

      icon: rowActionModel.active ? faCircleXmark : faCircleCheck,

      disabled: !selectedOrgData?.id || !rowActionModel.allowSourceActivation
    });

    // Delete
    items.push({
      key: 'delete',
      label: 'Delete',
      icon: faTrashAlt
    });

    // Resubmit
    if (rowActionModel.sourceTypeId !== SOURCE_TYPE.MICROSOFT) {
      items.push({
        key: 'resubmit',
        label: 'Resubmit',
        icon: faSync,
        disabled: !enableResubmit
      });
    }

    return items;
  }, [data, hasUpdatePermission, selectedOrgData]);

  if (!data || !hasUpdatePermission || !menuItems.length) {
    return null;
  }

  const isMenuOpen = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        className={`action-btn${isMenuOpen ? ' action-btn-menu-open' : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <FontAwesomeIcon
          icon={faEllipsisV}
          size="xs"
          style={{ margin: '1px' }}
        />
      </IconButton>

      <Menu
        className={classes.ellipsisMenu}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        disableRestoreFocus
        slotProps={{
          backdrop: {
            onClick: (e) => {
              e.stopPropagation();
              setAnchorEl(null);
            }
          }
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={() => {
              onMenuClick(item.key as ActionMenuType, data);

              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={item.icon} />
            </ListItemIcon>
            <ListItemText sx={{ minWidth: '6rem' }}>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
